import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { resetReducerState } from "../action"
import {
    addApplicationTemplateVersions,
    applicationTemplateToTreeMutation,
    convertApplicationTemplatesToTree
} from "../../utility/workshop/convertApplicationTemplatesToTree"
import { mutateTreeTyped } from "../../utility/common/tree"
import { removeCategoryFromCatalog } from "../../utility/knowledgeBase/categoriesTree"
import {
    ApplicationTemplate,
    ApplicationTemplateCategory,
    ApplicationTemplateMutation,
    ApplicationTemplateTreeData,
    ApplicationTemplateVersion
} from "../../models/workshop/template"
import { ItemId, TemplateId, TemplateVersionStatus, VersionId } from "../../models/workshop/common"
import { DeleteApplicationTemplateVersionRequest } from "../../models/workshop/api"
import { ApplicationInstance } from "../../models/workshop/instance"

export type WorkshopStateType = {
    applicationTemplates: ApplicationTemplate[]
    workshopCatalog: ApplicationTemplateCategory[]
    applicationTemplatesMap: Record<string, ApplicationTemplateTreeData | undefined>
    currentTempCategory: ApplicationTemplateCategory | undefined
    currentApplicationTemplate: ApplicationTemplate | undefined
    currentApplicationTemplateVersion: ApplicationTemplateVersion | undefined
    currentApplicationTemplateVersionId: VersionId
    releasedApplications: ApplicationInstance[]
    actualVersions: Record<string, ApplicationTemplateVersion>
    isApplicationTemplateCreating: boolean
    isApplicationTemplatesExist: boolean
    publishedTemplates: TemplateId[]
    queryString: string
    isEditMode: boolean
}

const initialState: WorkshopStateType = {
    applicationTemplates: [],
    workshopCatalog: [],
    applicationTemplatesMap: {},
    currentTempCategory: undefined,
    currentApplicationTemplate: undefined,
    currentApplicationTemplateVersion: undefined,
    currentApplicationTemplateVersionId: "",
    releasedApplications: [],
    actualVersions: {},
    isApplicationTemplateCreating: false,
    isApplicationTemplatesExist: false,
    publishedTemplates: [],
    queryString: "",
    isEditMode: false
}

const workshopSlice = createSlice({
    name: "workshop",
    initialState,
    reducers: {
        setApplicationTemplates(state, action: PayloadAction<ApplicationTemplate[]>) {
            state.applicationTemplates = action.payload
        },
        addApplicationTemplateVersion(state, action: PayloadAction<ApplicationTemplateVersion>) {
            const version = action.payload
            const id = version.TemplateId

            const templateIndex = state.workshopCatalog.findIndex(catalog => catalog.Id === id)
            if (templateIndex >= 0) {
                state.workshopCatalog[templateIndex].Versions.push(version)
            }

            const templateTree = state.applicationTemplatesMap[id]

            if (templateTree) {
                state.applicationTemplatesMap[id] = addApplicationTemplateVersions(id, templateTree, [version])
            }
        },
        updateApplicationTemplate(state, action: PayloadAction<ApplicationTemplate>) {
            const template = action.payload
            const templateTree = state.applicationTemplatesMap[template.Id]

            if (templateTree) {
                state.applicationTemplatesMap[template.Id] = mutateTreeTyped<ApplicationTemplateTreeData>(
                    templateTree,
                    template.Id,
                    applicationTemplateToTreeMutation(action.payload, templateTree.items[template.Id])
                )
            }
        },
        deleteApplicationTemplate(state, action: PayloadAction<string>) {
            const templateId = action.payload

            state.workshopCatalog = state.workshopCatalog.filter(template => template.Id !== templateId)

            if (state.currentApplicationTemplate?.Id === templateId) {
                state.currentApplicationTemplate = undefined
                state.currentApplicationTemplateVersion = undefined
                state.currentApplicationTemplateVersionId = ""
            }

            const copiedMap = { ...state.applicationTemplatesMap }
            delete copiedMap[templateId]
            state.applicationTemplatesMap = copiedMap
        },
        deleteApplicationTemplateVersion(state, action: PayloadAction<DeleteApplicationTemplateVersionRequest>) {
            const { templateId, versionId } = action.payload
            const templateIndex = state.workshopCatalog.findIndex(catalog => catalog.Id === templateId)

            if (templateIndex >= 0) {
                const versions = state.workshopCatalog[templateIndex].Versions
                state.workshopCatalog[templateIndex].Versions = versions.filter(version => version.Id !== versionId)
            }

            const templateTree = state.applicationTemplatesMap[templateId]
            if (templateTree) {
                state.applicationTemplatesMap[templateId] = removeCategoryFromCatalog<ApplicationTemplateTreeData>(
                    templateTree,
                    versionId,
                    templateId
                )
            }

            if (state.currentApplicationTemplateVersion?.Id === versionId) {
                state.currentApplicationTemplateVersion = undefined
                state.currentApplicationTemplateVersionId = ""
            }
        },
        setCurrentTempCategory(state, action: PayloadAction<ApplicationTemplateCategory | undefined>) {
            state.currentTempCategory = action.payload
        },
        setCurrentApplicationTemplate(state, action: PayloadAction<ApplicationTemplate | undefined>) {
            state.currentApplicationTemplate = action.payload
        },
        setCurrentApplicationTemplateVersion(state, action: PayloadAction<ApplicationTemplateVersion | undefined>) {
            state.currentApplicationTemplateVersion = action.payload
        },
        setCurrentApplicationTemplateVersionId(state, action: PayloadAction<string>) {
            state.currentApplicationTemplateVersionId = action.payload
        },
        toggleCategoryExpanded(state, action: PayloadAction<{ id: ItemId; isExpanded: boolean }>) {
            const { id, isExpanded } = action.payload

            const templateTree = state.applicationTemplatesMap[id]
            if (templateTree) {
                state.applicationTemplatesMap[id] = mutateTreeTyped<ApplicationTemplateTreeData>(templateTree, id, {
                    isExpanded
                })
            }
        },
        changeApplicationTemplateStatus(state, action: PayloadAction<ApplicationTemplateMutation>) {
            const { Id, VersionId, Status } = action.payload

            const template = state.applicationTemplatesMap[Id]

            if (!template || !VersionId) {
                return
            }

            if (Status === TemplateVersionStatus.PUBLISH || Status === TemplateVersionStatus.RELEASE) {
                state.publishedTemplates.push(Id)
            } else {
                state.publishedTemplates = state.publishedTemplates.filter(templateId => templateId !== Id)
            }

            const catalogIndex = state.workshopCatalog.findIndex(catalog => catalog.Id === Id)
            if (catalogIndex >= 0 && Status) {
                const catalog = state.workshopCatalog[catalogIndex]
                const versionIndex = catalog.Versions.findIndex(version => version.Id === VersionId)

                if (versionIndex >= 0) {
                    const version = state.workshopCatalog[catalogIndex].Versions[versionIndex]
                    state.workshopCatalog[catalogIndex].Versions[versionIndex] = {
                        ...version,
                        Status: Status
                    }
                }
            }

            const applicationTemplateItem = template.items[VersionId]
            const templateMap = state.applicationTemplatesMap[Id]

            if (templateMap) {
                state.applicationTemplatesMap[Id] = mutateTreeTyped<ApplicationTemplateTreeData>(
                    templateMap,
                    VersionId,
                    applicationTemplateToTreeMutation(action.payload, applicationTemplateItem)
                )
            }

            if (Status && state.currentApplicationTemplateVersion) {
                state.currentApplicationTemplateVersion = {
                    ...state.currentApplicationTemplateVersion,
                    Status
                }
            }
        },
        setApplicationTemplateCreating(state, action: PayloadAction<boolean>) {
            state.isApplicationTemplateCreating = action.payload
        },
        setApplicationTemplatesExist(state, action: PayloadAction<boolean>) {
            state.isApplicationTemplatesExist = action.payload
        },
        addPublishedVersion(state, action: PayloadAction<string>) {
            state.publishedTemplates.push(action.payload)
        },
        removePublishedVersion(state, action: PayloadAction<string>) {
            state.publishedTemplates = state.publishedTemplates.filter(templateId => templateId !== action.payload)
        },
        deletePublishedVersion(state) {
            state.publishedTemplates = []
        },
        setQueryString(state, action: PayloadAction<string>) {
            state.queryString = action.payload
        },
        setEditMode(state, action: PayloadAction<boolean>) {
            state.isEditMode = action.payload
        },
        setApplicationTemplateCatalog(state, action: PayloadAction<ApplicationTemplateCategory[]>) {
            state.workshopCatalog = action.payload

            action.payload.forEach(catalog => {
                const publishedVersion = catalog.Versions.find(version => {
                    return (
                        version.Status === TemplateVersionStatus.PUBLISH ||
                        version.Status === TemplateVersionStatus.RELEASE
                    )
                })

                if (publishedVersion) {
                    state.publishedTemplates.push(catalog.Id)
                }
            })

            const { treesMap, trees } = convertApplicationTemplatesToTree(action.payload)
            state.applicationTemplatesMap = treesMap
        },
        addApplicationTemplateCategory(state, action: PayloadAction<ApplicationTemplateCategory>) {
            state.workshopCatalog.push(action.payload)

            const { treesMap, trees } = convertApplicationTemplatesToTree([action.payload])
            state.applicationTemplatesMap = {
                ...state.applicationTemplatesMap,
                ...treesMap
            }
        },
        removeApplicationTemplateCategory(state, action: PayloadAction<string>) {
            state.workshopCatalog = state.workshopCatalog.filter(category => category.Id !== action.payload)
        },
        setReleasedApplications(state, action: PayloadAction<ApplicationInstance[]>) {
            state.releasedApplications = action.payload
        },
        addReleasedApplication(state, action: PayloadAction<ApplicationInstance>) {
            state.releasedApplications.push(action.payload)
        },
        removeReleasedApplication(state, action: PayloadAction<string>) {
            state.releasedApplications = state.releasedApplications.filter(
                application => application.Id !== action.payload
            )
        },
        setActualVersions(state, action: PayloadAction<ApplicationTemplateVersion[]>) {
            const versions = action.payload

            versions.forEach(version => {
                state.actualVersions[version.TemplateId] = version
            })
        },
        addActualVersion(state, action: PayloadAction<ApplicationTemplateVersion>) {
            const version = action.payload

            state.actualVersions[version.TemplateId] = version
        },
        removeActualVersion(state, action: PayloadAction<string>) {
            delete state.actualVersions[action.payload]
        },
        updateApplicationInstance(state, action: PayloadAction<ApplicationInstance>) {
            const application = action.payload
            const applicationIndex = state.releasedApplications.findIndex(app => app.Id === application.Id)

            if (applicationIndex > -1) {
                state.releasedApplications[applicationIndex] = application
            }
        }
    },
    extraReducers(builder) {
        builder.addCase(resetReducerState, () => {
            return initialState
        })
    }
})

export const WorkshopReducer = workshopSlice.reducer
export const workshopActions = workshopSlice.actions
